import { useContext } from 'react'
import useSelector from '@hooks/useSelector'
import useDispatch from '@hooks/useDispatch'

import { useForm, UseFormOptions, useLatestEntity } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import defaultState, { ProviderPackageItemModel, requiredFields } from '@models/providerPackageItem'
import type { ProviderPriceListModel } from '@models/types'

import * as providerPackageItemActions from '@redux/modules/providerPackageItem'
import { AppDispatch } from '@redux/store'

export const generateUrls = (providerPackageItem: Partial<ProviderPackageItemModel> = {}) => {
  const { service_product_id } = providerPackageItem

  return {
    serviceProductUrl: `#/systemManager/products/${service_product_id}/edit`,
  }
}

type AddProviderPackageItemParams = {
  providerPackageItem: ProviderPackageItemModel,
  showCreateProviderPackageItemModal: (arg: ProviderPackageItemModel) => void,
}

const addProviderPackageItem = (params: AddProviderPackageItemParams) => new Promise((resolve, reject) => {
  const { providerPackageItem, showCreateProviderPackageItemModal } = params

  if (showCreateProviderPackageItemModal){
    const payload = {
      providerPackageItem,
    }

    // @ts-expect-error TS2345
    showCreateProviderPackageItemModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateProviderPackageItemModal not defined in PageContext callbacks'))
})

type CreateProviderPackageItemParams = {
  dispatch: AppDispatch,
  providerPackageItemParams?: Partial<ProviderPackageItemModel>,
}

const createProviderPackageItem = (params: CreateProviderPackageItemParams) => {
  const { providerPackageItemParams, dispatch } = params
  const { createProviderPackageItem: createFn } = providerPackageItemActions

  // @ts-expect-error TS2345
  return dispatch(createFn(providerPackageItemParams))
}

type DeleteProviderPackageItemParams = {
  dispatch: AppDispatch,
  providerPackageItem: ProviderPackageItemModel,
}

const deleteProviderPackageItem = (params: DeleteProviderPackageItemParams) => {
  const { providerPackageItem, dispatch } = params
  const { deleteProviderPackageItem: deleteFn } = providerPackageItemActions

  return dispatch(deleteFn(providerPackageItem))
}

type DuplicateProviderPackageItemParams = {
  dispatch: AppDispatch,
  providerPackageItem: ProviderPackageItemModel,
}

const duplicateProviderPackageItem = (params: DuplicateProviderPackageItemParams) => {
  const { dispatch, providerPackageItem } = params
  const { duplicateProviderPackageItem: duplicateFn } = providerPackageItemActions

  return dispatch(duplicateFn(providerPackageItem))
}

export function useRelations(providerPackageItem: ProviderPackageItemModel) {
  const { provider_price_list_id } = providerPackageItem || {}

  const entities = useSelector(reduxState => reduxState.entities)
  const { providerPriceLists } = entities

  const providerPriceList: Partial<ProviderPriceListModel> = providerPriceLists[provider_price_list_id] || {}

  return {
    providerPriceList,
  }
}
type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useProviderPackageItemForm(
  providerPackageItem: Partial<ProviderPackageItemModel> = {},
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { validateOn } = options

  const providerPackageItemForm = useForm(
    defaultState,
    { entity: providerPackageItem, requiredFields, validateOn },
    [providerPackageItem.id, providerPackageItem.cache_key],
  )

  return {
    ...providerPackageItemForm,
  }
}

function useProviderPackageItem(initEntity: Partial<ProviderPackageItemModel> = {}) {
  // @ts-expect-error TS2322
  const latestEntity: { entity: ProviderPackageItemModel } = useLatestEntity(initEntity, 'providerPackageItems')
  const { entity: providerPackageItem } = latestEntity

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)
  const { showCreateProviderPackageItemModal } = callbacks || {}

  const { providerPriceList } = useRelations(providerPackageItem)

  const { creating, deleting, loading } = useSelector(reduxState => reduxState.providerPackageItems)

  return {
    callbacks: {
      addProviderPackageItem: () => (
        addProviderPackageItem({ providerPackageItem, showCreateProviderPackageItemModal })
      ),
      createProviderPackageItem: (providerPackageItemParams: Partial<ProviderPackageItemModel>) => (
        createProviderPackageItem({ providerPackageItemParams, dispatch })
      ),
      deleteProviderPackageItem: () => deleteProviderPackageItem({ providerPackageItem, dispatch }),
      duplicateProviderPackageItem: () => duplicateProviderPackageItem({
        providerPackageItem, dispatch,
      }),
    },
    creating,
    deleting,
    loading,
    providerPackageItem,
    providerPriceList,
    urls: generateUrls(providerPackageItem),
  }
}

export default useProviderPackageItem
